import $ from '@nuskin/nuskinjquery';
import {PriceType, Product} from '@nuskin/ns-product-lib';
import {RunConfigService, util, events, BrowserDetection, UrlService} from '@nuskin/ns-util';
import {UserService, AccountManager} from '@nuskin/ns-account';
import ProductStatusService from '../product/ProductStatusService.js';
import _ from 'lodash';
import { getConfiguration } from '@nuskin/configuration-sdk';
import axios from 'axios';

let ProductService = function() {
    // ---------------------------------------------
    //
    //        Public Methods
    //
    // ---------------------------------------------
    let publicMethods = {
        /**
         *
         * @param options - sku, cntryCd, language
         * @param callbackFn
         * @returns {null}
         */
        getProductBySku: async function(_options) {
            let options = verifyLocaleFields(_options),
                promises = [getProductDetail(options)];

            promises.push(getProductStatus(options));

            let values = await Promise.all(promises);

            let result = await handleDetailResponse(values[0], options);

            if (values[1] && values[1][0]) {
                handleStatusResponse(result.product, values[1][0], options);
            } else {
                // Fallback for product quantity on a failed product status call.
                // This is done to prevent the product detail component from locking up unnecessarily.
                result.product.availableQuantity = 10000;
            }

            return result;
        },

        /**
         * Retrieves product data from PMD - /content/products
         * Full URL used to get product data ex. https://test.nuskin.com/content/products/01/11/11/01111155/en.service.US.json
         * @param {string} sku - ex. 01111155
         * @returns {Promise}
         */
        getProductData: function(sku, country, language) {
            if(!sku) return;
            let optionsData = country ? {country} : {};
            if(language)
                optionsData.language = language;

            let tokenizedSku = tokenizeSku(sku),
                url,
                options = verifyLocaleFields(optionsData);

            url = UrlService.getSiteUrl() + "/content/products/" + tokenizedSku + "/" +
                options.language + ".service." + options.country + ".json";

            return new Promise((resolve, reject) => {
                $.ajax({
                    type: 'GET',
                    url: url,
                    success: function(response) {
                        resolve(createDataFromSkuSearch(response, options));
                    },
                    error: function(response) {
                        console.error("There was a problem retrieving product data:", response);
                        reject(response);
                    }
                });
            });



        }
    };

    // ---------------------------------------------
    //
    //        Private Methods
    //
    // ---------------------------------------------
    async function getProductDetail(_options) {
        let options = _options;
        let url = await getSkuSearchUrl(options);
        let data;

        // Hit the PMD and get all the data for the product including the price information.
        // Example url: https://test.nuskin.com/content/products/01/00/34/01003440.service.US.json
        try {
            data = await axios({
                method: 'GET',
                url
            })
        } catch (e) {
            throw {error: e, url: url, failedSku: options.sku, type: events.errors.PRODUCT_LOOKUP};
        }
        return data.data;
    }

    function getProductStatus(_options) {
        let options = _options;
        let queryParams = { filter: "atp,pricing" };
        const agelocme = !!options.agelocme && options.agelocme.code;
        if (agelocme) {
            queryParams.agelocme = agelocme;
        }
        return (
            ProductStatusService.getProductStatus(
                options.sku,
                options.country,
                queryParams
            )
            /*
            Fallback on a failed status call. The reason for this is that the product status service is currently
            unreliable, and the getProduct function that depends on this also makes a request to the product detail
            service, which (1) is more reliable and (2) also returns pricing data (though it doesn't return live
            pricing).
             */
                .catch(() => null)
        );
    }

    async function getSkuSearchUrl(options) {
        let firstTwo = options.sku.substring(0, 2),
            secondTwo = options.sku.substring(2, 4),
            thirdTwo = options.sku.substring(4, 6),
            runConfig = RunConfigService.getRunConfig(),
            query = '',
            skuSearchUrl;

        const {Url: urlCfg} = await getConfiguration(['Url']);
        skuSearchUrl = urlCfg.skuSearchUrl;

        if (runConfig.baseUrl !== '' && !skuSearchUrl.startsWith('http')) {
            query = runConfig.baseUrl;
        }

        if (UserService.isLTOSite()){
            query += util.getSiteConfig().urls.skuSearchUrl[0].replace('{firstTwo}', firstTwo);
        }
        else{
            query += skuSearchUrl.replace('{firstTwo}', firstTwo);
        }

        query = query.replace('{secondTwo}', secondTwo);
        query = query.replace('{thirdTwo}', thirdTwo);
        query = query.replace('{sku}', options.sku);
        query = query.replace('{countryCd}', options.cntryCd ? options.cntryCd : options.country);

        return query;
    }

    async function handleDetailResponse(responseData, options) {
        let retVal = {success: true},
            dataForProduct = createDataFromSkuSearch(responseData, options);

        if (dataForProduct.sku) {
            retVal.product = new Product(dataForProduct);

            const siteUrl = UrlService.getSiteUrl();
            const isEdge = BrowserDetection.isEdge();
            const isFirefox = BrowserDetection.isFirefox();

            // set the base url for images, etc
            retVal.product.setBaseUrl(siteUrl);

            // fix image paths for non-edge/firefox
            if (!isEdge && !isFirefox) {
                retVal.product.fullImage = retVal.product.fullImage
                    ? `${retVal.product.fullImage.split("?")[0]}?format=pjpg`
                    : undefined;
                retVal.product.thumbnail = retVal.product.thumbnail
                    ? `${retVal.product.thumbnail.split("?")[0]}?format=pjpg`
                    : undefined;
            }
        } else {
            retVal.success = false;
            retVal.failedSku = options.sku;
        }

        if (retVal.product && options.priceType) {
            const {Cart: cartCfg} = await getConfiguration(['Cart']);
            const priceType = cartCfg.showWholeSalePricing && !AccountManager.isLoggedIn()
                ? PriceType.WWHL
                : options.priceType;
            retVal.product.setPriceAndPvFromType(priceType);
        }

        return retVal;
    }

    function handleStatusResponse(product, statusResponse) {
        if (statusResponse) {
            product.locallyProduced = statusResponse.locallyProduced;
            product.setMarketAttributes(statusResponse);
            product.addPricingFromStatus(statusResponse);
        }
    }

    function createDataFromSkuSearch(searchData, options) {
        if (_.isEmpty(searchData)) {
            return new Product();
        }
        let product = new Product(),
            langObj = getLangObj(searchData['contents']['language'], options.language),
            marketObj = searchData['market'];
        if (Array.isArray(marketObj)) {
            marketObj = getMarketObj(searchData['market'], options.country);
        }
        let mktOrderTypes = (typeof marketObj !== "undefined" && marketObj && marketObj.hasOwnProperty("orderType")) ? marketObj["orderType"] : null,
            mktCustTypes = (typeof marketObj !== "undefined" && marketObj && marketObj.hasOwnProperty("custType")) ? marketObj["custType"] : null;

        let restrictedMarkets = (typeof marketObj !== "undefined" && marketObj && marketObj.hasOwnProperty("restrictedMarket"))
            ?
            marketObj["restrictedMarket"]
            :
            null;

        let addOns = (typeof marketObj !== "undefined" && marketObj && marketObj.hasOwnProperty("addOns")) ? marketObj["addOns"] : null;

        if (marketObj !== null) {
            product.sku = searchData.sku;
            product.isExclusive = searchData.hasOwnProperty("isExclusive") ? searchData.isExclusive : false;
            if (searchData.hasOwnProperty("contents")) {
                product.setFullImage(searchData['contents']['fullImage']);
                product.setThumbnail(searchData['contents']['fullImage'].replace(/\.img\.[0-9]+\.[0-9]+\.png/i, ".img.160.160.png"));
                if (searchData.contents.hasOwnProperty("imageCarousel")) {
                    product.setProductCarouselImages(searchData['contents']['imageCarousel']);
                }
            }
            // AEM-8320 - it returns two BP (08007070 and 08129080), 08129080 does not have any translation yet.
            // Note that langObj can be null (have a BP that does not have any translation)

            product.productLabels = marketObj['populateProductLabels'];
            if (langObj) {
                product.lang = langObj['languageCode'];
                product.title = langObj['name'];
                product.shortDescr = langObj['shortDescription'];
                product.longDescr = langObj['longDescription'];
                product.ingredients = langObj['ingredients'];
                product.benefits = langObj['benefits'];
                product.usage = langObj['usage'];
                product.resources = langObj['resources'];
                product.videos = langObj['videos'];
                product.contentSection = langObj['contentSection'];
                product.sizeWeight = langObj['size_weight'];
                product.nettoWeight = langObj['netto_weight'];
                product.salesLabel = langObj['salesLabel'];
                product.movie = langObj['movie'];
                product.youtube = langObj['youtube'];
                product.salesEventText = langObj['tdc_salesEventText'];
                if (langObj.variantInfo) {
                    if (langObj.variantInfo.tdc_dropdownLabel) {
                        product.variantDropdownLabel = langObj.variantInfo.tdc_dropdownLabel
                    }
                    if (langObj.variantInfo.tdc_dropdownPlaceholder) {
                        product.variantDropdownPlaceholder = langObj.variantInfo.tdc_dropdownPlaceholder;
                    }
                    if (langObj.variantInfo.tdc_variantsLabel) {
                        product.variantsLabel = langObj.variantInfo.tdc_variantsLabel;
                    }
                }
            }
            product.setImageAltText(searchData['contents']['altText']);
            product.country = marketObj['countryCode'];
            product.scanQualified = marketObj['scanQualified'];
            product.division = searchData['division'];

            if (mktOrderTypes) {
                product.orderTypes = mktOrderTypes;
            }
            if (mktCustTypes) {
                product.custTypes = mktCustTypes;
            }
            if (restrictedMarkets) {
                product.restrictedMarkets = restrictedMarkets;
            }
            if (addOns) {
                product.addOns = addOns;
            } else {
                product.addOns = [];
            }

            if (options.isGroupOffer)
                product.isGroupOffer = options.isGroupOffer;
            if (options.isPersonalOffer)
                product.isPersonalOffer = options.isPersonalOffer;
        }

        if (searchData.variantConfig) {
            if (searchData.variantConfig.variantType) {
                product.variantType = searchData.variantConfig.variantType;
            }
            if (searchData.variantConfig.variants) {
                searchData.variantConfig.variants.forEach((variant) => {
                    product.setVariant(createDataFromSkuSearch(variant, options));
                });
            }
            if (searchData.variantConfig.swatchColor) {
                product.shade = searchData.variantConfig.swatchColor;
            }
            if (searchData.variantConfig.baseSku) {
                product.baseSku = searchData.variantConfig.baseSku;
            }
        }

        return product;
    }

    function getLangObj(langDataArray, language) {
        let retObj = null,
            langObj = null,
            enLangObj = null;

        for (let i = 0; i < langDataArray.length; i++) {
            langObj = langDataArray[i];
            if (langObj['languageCode'] === language) {
                retObj = langDataArray[i];
            }
            if (langObj['languageCode'] == 'en') {
                enLangObj = langDataArray[i];
            }
        }
        if (retObj === null) {
            retObj = enLangObj;
        }

        return retObj;
    }

    function getMarketObj(marketDataArray, country) {
        let retObj = null,
            marketObj = null,
            usMarketObj = null;

        for (let i = 0; i < marketDataArray.length; i++) {
            marketObj = marketDataArray[i];
            if (marketObj['countryCode'] === country) {
                retObj = marketDataArray[i];
            }
            if (marketObj['countryCode'] === 'US') {
                usMarketObj = marketDataArray[i];
            }
        }
        if (retObj === null) {
            retObj = usMarketObj;
        }

        return retObj;
    }

    /**
     * Returns string needed for getting product data from PMD - ex. 01/11/11/01111155
     * Example full url where this is used: https://test.nuskin.com/content/products/01/11/11/01111155/en.service.US.json
     * @param {string} sku - ex. 01111155
     * @returns tokenized sku
     */
    function tokenizeSku(sku) {
        let part1 = sku.substring(0, 2);
        let part2 = sku.substring(2, 4);
        let part3 = sku.substring(4, 6);
        return part1 + "/" + part2 + "/" + part3 + "/" + sku;
    }

    function verifyLocaleFields(options) {
        let runConfig = RunConfigService.getRunConfig();

        options.country = options.cntryCd ? options.cntryCd :
            options.country ? options.country :
                runConfig.country;
        options.language = options.language ? options.language : runConfig.language;
        return options;
    }

    return publicMethods;
}();

export default ProductService;
